import { useRef, useState } from "react";
import axios from "axios";
import { ArrowRight, ArrowLeft, Check, Loader2, FileUp, X, FileText } from "lucide-react";
import { SITE } from "@/lib/site";
import { useLang } from "../i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_FILE_MB = SITE.maxUploadMb;
const ACCEPTED = ".pdf,.jpg,.jpeg,.png";

const STEPS = [
    "Your Details",
    "Medical Information",
    "Documents",
    "Additional Information",
    "Review & Submit",
];

const REASONS = [
    "Specialist consultation",
    "Second opinion",
    "Treatment cost information",
    "Travel planning",
    "Other",
];

const TIMEFRAMES = ["Within 2–3 weeks", "1–3 months", "3–6 months", "Flexible / not sure"];

const SPECIALTY_OPTS = ["Cardiology", "Orthopaedics", "Oncology", "Fertility", "Other / not sure yet"];

const initialData = {
    fullName: "",
    email: "",
    phone: "",
    country: "",
    city: "",
    dob: "",
    specialty: "",
    condition: "",
    diagnosis: "",
    reason: "",
    files: [],
    travelTimeframe: "",
    accompanying: "",
    notes: "",
    consent: false,
};

// Multi-step medical opinion experience. Development note: documents are NOT
// uploaded or stored — only file names/sizes are collected as metadata so that
// secure storage can be added later without changing this flow.
export const MultiStepOpinionForm = () => {
    const { t, lang } = useLang();
    const STEP_LABELS = [
        t("form.step1", "Your Details"),
        t("form.step2", "Medical Information"),
        t("form.step3", "Documents"),
        t("form.step4", "Additional Information"),
        t("form.step5", "Review & Submit"),
    ];
    const [step, setStep] = useState(0);
    const [data, setData] = useState(initialData);
    const [errors, setErrors] = useState({});
    const [fileError, setFileError] = useState("");
    const [status, setStatus] = useState("idle");
    const [reference, setReference] = useState("");
    const [serverError, setServerError] = useState("");
    const fileInput = useRef(null);

    const set = (key, value) => setData((d) => ({ ...d, [key]: value }));

    const validate = (s) => {
        const e = {};
        if (s === 0) {
            if (!data.fullName.trim()) e.fullName = t("form.errFullName", "Please enter your full name.");
            if (!/^\S+@\S+\.\S+$/.test(data.email)) e.email = t("form.errEmail", "Please enter a valid email address.");
            if (!data.phone.trim()) e.phone = t("form.errPhone", "Please enter a phone or WhatsApp number.");
            if (!data.country.trim()) e.country = t("form.errCountry", "Please enter your country of residence.");
        }
        if (s === 1) {
            if (!data.specialty) e.specialty = t("form.errSpecialty", "Please choose an area of care.");
            if (!data.condition.trim()) e.condition = t("form.errCondition", "Please describe your condition or concern.");
            if (!data.reason) e.reason = t("form.errReason", "Please tell us the reason for your enquiry.");
        }
        if (s === 4 && !data.consent) e.consent = t("form.errConsent", "Your consent is required to submit.");
        return e;
    };

    const next = () => {
        const e = validate(step);
        setErrors(e);
        if (Object.keys(e).length === 0) setStep((v) => Math.min(v + 1, STEPS.length - 1));
    };

    const back = () => {
        setErrors({});
        setStep((v) => Math.max(v - 1, 0));
    };

    const addFiles = (fileList) => {
        setFileError("");
        const added = [];
        for (const f of fileList) {
            const okType = /\.(pdf|jpe?g|png)$/i.test(f.name);
            if (!okType) {
                setFileError(
                    t("form.fileTypeErr", "{name} is not supported. Please upload PDF, JPG or PNG files.")
                        .replace("{name}", f.name)
                );
                continue;
            }
            if (f.size > MAX_FILE_MB * 1024 * 1024) {
                setFileError(
                    t("form.fileSizeErr", "{name} is larger than {mb} MB. Please reduce the size or split the document.")
                        .replace("{name}", f.name)
                        .replace("{mb}", String(MAX_FILE_MB))
                );
                continue;
            }
            added.push({ name: f.name, size: f.size });
        }
        if (added.length) {
            setData((d) => ({ ...d, files: [...d.files, ...added].slice(0, 10) }));
        }
    };

    const onSubmit = async () => {
        const e = validate(4);
        setErrors(e);
        if (Object.keys(e).length) return;
        setStatus("loading");
        setServerError("");
        try {
            const res = await axios.post(`${API}/enquiries`, {
                service: "Medical Opinion",
                source: "multi-step-form",
                preferred_language: lang,
                name: data.fullName,
                email: data.email,
                phone: data.phone,
                country: data.country,
                city: data.city,
                date_of_birth: data.dob || null,
                specialty: data.specialty,
                message: data.condition,
                reason: data.reason,
                travel_timeframe: data.travelTimeframe || null,
                accompanying: data.accompanying || null,
                notes: data.diagnosis ? `Current diagnosis/treatment: ${data.diagnosis}${data.notes ? `\n\nNotes: ${data.notes}` : ""}` : data.notes || null,
                documents: data.files,
                consent: data.consent,
            });
            const ref = res.data?.case_id || "";
            setReference(ref);
            setStatus("success");
        } catch (err) {
            setStatus("error");
            setServerError(
                t("form.errorMsg", "Something went wrong while sending your enquiry. Please try again, or reach us on WhatsApp.")
            );
        }
    };

    if (status === "success") {
        return (
            <div
                data-testid="msf-success"
                className="bg-white border border-pine/15 rounded-2xl p-8 sm:p-12 text-center"
            >
                <span className="mx-auto flex items-center justify-center w-14 h-14 rounded-xl bg-sage text-forest">
                    <Check className="w-6 h-6" />
                </span>
                <h3 className="font-serif text-3xl mt-6">{t("form.enquiryReceived", "Enquiry received")}</h3>
                <p className="mt-3 text-moss leading-relaxed max-w-md mx-auto">
                    {t("form.successNote", "Thank you for trusting UniversHeal. Our team will review your details and reach out to you shortly.")}
                </p>
                {reference && (
                    <p data-testid="msf-reference" className="mt-5 inline-block bg-sage text-forest text-sm font-bold tracking-wider px-4 py-2 rounded-lg">
                        {t("form.yourReference", "Your reference:")} {reference}
                    </p>
                )}
                <button
                    type="button"
                    data-testid="msf-reset"
                    onClick={() => {
                        setData(initialData);
                        setStep(0);
                        setStatus("idle");
                    }}
                    className="block mx-auto mt-8 text-sm font-semibold text-pine underline underline-offset-4 hover:text-forest"
                >
                    {t("form.sendAnother", "Send another enquiry")}
                </button>
            </div>
        );
    }

    const err = (key) =>
        errors[key] && (
            <p role="alert" data-testid={`msf-error-${key}`} className="mt-2 text-xs font-semibold text-red-800">
                {errors[key]}
            </p>
        );

    return (
        <div
            data-testid="multi-step-opinion-form"
            className="bg-white border border-pine/15 rounded-2xl p-6 sm:p-10 shadow-[0_20px_60px_rgba(6,60,53,0.06)]"
        >
            {/* progress */}
            <div aria-hidden="true">
                <div className="flex items-center justify-between text-[11px] font-bold uppercase tracking-widest2 text-moss">
                    <span data-testid="msf-step-label">
                        {t("form.stepProgress", "Step {x} of {y}").replace("{x}", step + 1).replace("{y}", STEPS.length)}
                    </span>
                    <span className="hidden sm:block">{STEP_LABELS[step]}</span>
                </div>
                <div className="mt-3 h-1 bg-sage rounded-full overflow-hidden">
                    <div
                        className="h-full bg-forest rounded-full transition-all duration-500"
                        style={{ width: `${((step + 1) / STEPS.length) * 100}%` }}
                    />
                </div>
                <ol className="mt-4 hidden md:flex items-center justify-between">
                    {STEPS.map((label, i) => (
                        <li key={label} className="flex items-center gap-2">
                            <span
                                className={`flex items-center justify-center w-6 h-6 rounded-md text-[11px] font-bold ${
                                    i < step
                                        ? "bg-forest text-ivory"
                                        : i === step
                                        ? "bg-pine text-ivory"
                                        : "bg-sage text-moss"
                                }`}
                            >
                                {i < step ? <Check className="w-3 h-3" /> : i + 1}
                            </span>
                            <span
                                className={`text-xs ${
                                    i === step ? "font-bold text-forest" : "text-moss"
                                }`}
                            >
                                {label}
                            </span>
                        </li>
                    ))}
                </ol>
            </div>

            <div className="mt-8">
                {step === 0 && (
                    <fieldset data-testid="msf-step-1">
                        <legend className="font-serif font-medium text-2xl sm:text-3xl text-forest">
                            {t("form.legend1", "Tell us about yourself")}
                        </legend>
                        <div className="mt-6 grid sm:grid-cols-2 gap-5">
                            <div className="sm:col-span-2">
                                <label htmlFor="msf-name" className="label-field">{t("form.fullNameReq", "Full name *")}</label>
                                <input id="msf-name" data-testid="msf-field-fullName" className="input-field" value={data.fullName}
                                    onChange={(e) => set("fullName", e.target.value)} placeholder={t("form.namePlaceholder", "Your full name")} autoComplete="name" />
                                {err("fullName")}
                            </div>
                            <div>
                                <label htmlFor="msf-email" className="label-field">{t("form.emailReq", "Email *")}</label>
                                <input id="msf-email" data-testid="msf-field-email" type="email" className="input-field" value={data.email}
                                    onChange={(e) => set("email", e.target.value)} placeholder="you@example.com" autoComplete="email" />
                                {err("email")}
                            </div>
                            <div>
                                <label htmlFor="msf-phone" className="label-field">{t("form.phoneReq", "Phone / WhatsApp *")}</label>
                                <input id="msf-phone" data-testid="msf-field-phone" type="tel" className="input-field" value={data.phone}
                                    onChange={(e) => set("phone", e.target.value)} placeholder={t("form.phonePlaceholder", "Include country code")} autoComplete="tel" />
                                {err("phone")}
                            </div>
                            <div>
                                <label htmlFor="msf-country" className="label-field">{t("form.countryReq", "Country of residence *")}</label>
                                <input id="msf-country" data-testid="msf-field-country" className="input-field" value={data.country}
                                    onChange={(e) => set("country", e.target.value)} placeholder={t("form.countryPlaceholder", "Where are you writing from?")} autoComplete="country-name" />
                                {err("country")}
                            </div>
                            <div>
                                <label htmlFor="msf-city" className="label-field">{t("form.city", "City")}</label>
                                <input id="msf-city" data-testid="msf-field-city" className="input-field" value={data.city}
                                    onChange={(e) => set("city", e.target.value)} placeholder={t("form.cityPlaceholder", "Your city")} autoComplete="address-level2" />
                            </div>
                            <div>
                                <label htmlFor="msf-dob" className="label-field">{t("form.dob", "Date of birth")}</label>
                                <input id="msf-dob" data-testid="msf-field-dob" type="date" className="input-field" value={data.dob}
                                    onChange={(e) => set("dob", e.target.value)} />
                            </div>
                        </div>
                    </fieldset>
                )}

                {step === 1 && (
                    <fieldset data-testid="msf-step-2">
                        <legend className="font-serif font-medium text-2xl sm:text-3xl text-forest">
                            {t("form.legend2", "About your medical situation")}
                        </legend>
                        <div className="mt-6 grid gap-5">
                            <div>
                                <label htmlFor="msf-specialty" className="label-field">{t("form.areaOfCareReq", "Area of care *")}</label>
                                <select id="msf-specialty" data-testid="msf-field-specialty" className="input-field"
                                    value={data.specialty} onChange={(e) => set("specialty", e.target.value)}>
                                    <option value="">{t("form.selectSpecialty", "Select a specialty")}</option>
                                    {SPECIALTY_OPTS.map((s, i) => (
                                        <option key={s} value={s}>
                                            {t(["form.specialty.cardiology", "form.specialty.orthopaedics", "form.specialty.oncology", "form.specialty.fertility", "form.specialty.other"][i], s)}
                                        </option>
                                    ))}
                                </select>
                                {err("specialty")}
                            </div>
                            <div>
                                <label htmlFor="msf-condition" className="label-field">{t("form.conditionLabel", "Your condition or concern *")}</label>
                                <textarea id="msf-condition" data-testid="msf-field-condition" rows={4} className="input-field resize-y"
                                    value={data.condition} onChange={(e) => set("condition", e.target.value)}
                                    placeholder={t("form.conditionPlaceholder", "A short summary of the condition, symptoms and when they started.")} />
                                {err("condition")}
                            </div>
                            <div>
                                <label htmlFor="msf-diagnosis" className="label-field">{t("form.diagnosisLabel", "Current diagnosis or treatment (if any)")}</label>
                                <textarea id="msf-diagnosis" data-testid="msf-field-diagnosis" rows={3} className="input-field resize-y"
                                    value={data.diagnosis} onChange={(e) => set("diagnosis", e.target.value)}
                                    placeholder={t("form.diagnosisPlaceholder", "Diagnoses received, medicines, surgeries or treatments so far.")} />
                            </div>
                            <div>
                                <label htmlFor="msf-reason" className="label-field">{t("form.reasonLabel", "Reason for this enquiry *")}</label>
                                <select id="msf-reason" data-testid="msf-field-reason" className="input-field"
                                    value={data.reason} onChange={(e) => set("reason", e.target.value)}>
                                    <option value="">{t("form.selectReason", "Select a reason")}</option>
                                    {REASONS.map((r, i) => (
                                        <option key={r} value={r}>{t(`form.reason${i + 1}`, r)}</option>
                                    ))}
                                </select>
                                {err("reason")}
                            </div>
                        </div>
                    </fieldset>
                )}

                {step === 2 && (
                    <fieldset data-testid="msf-step-3">
                        <legend className="font-serif font-medium text-2xl sm:text-3xl text-forest">
                            {t("form.legend3", "Medical documents")}
                        </legend>
                        <p className="mt-4 text-sm leading-relaxed text-moss">
                            {t("form.documentsNote", "Reports, scans and discharge summaries help our specialists understand your case. You can also share them later — this step is optional.")}
                        </p>
                        <button
                            type="button"
                            data-testid="msf-upload-button"
                            onClick={() => fileInput.current?.click()}
                            className="mt-6 w-full border border-dashed border-pine/30 rounded-xl px-6 py-10 text-center hover:bg-ivory transition-colors cursor-pointer"
                        >
                            <FileUp className="w-8 h-8 mx-auto text-natural" />
                            <span className="mt-3 block text-sm font-semibold text-forest">
                                {t("form.uploadReports", "Upload reports")}
                            </span>
                            <span className="mt-1 block text-xs text-moss">
                                {t("form.uploadHint", "PDF, JPG or PNG — up to {mb} MB each").replace("{mb}", MAX_FILE_MB)}
                            </span>
                        </button>
                        <input
                            ref={fileInput}
                            data-testid="msf-file-input"
                            type="file"
                            multiple
                            accept={ACCEPTED}
                            className="hidden"
                            onChange={(e) => {
                                addFiles(Array.from(e.target.files || []));
                                e.target.value = "";
                            }}
                        />
                        {fileError && (
                            <p role="alert" data-testid="msf-file-error" className="mt-3 text-xs font-semibold text-red-800">
                                {fileError}
                            </p>
                        )}
                        {data.files.length > 0 && (
                            <ul className="mt-5 space-y-2" data-testid="msf-file-list">
                                {data.files.map((f, i) => (
                                    <li
                                        key={f.name + i}
                                        data-testid={`msf-file-${i}`}
                                        className="flex items-center gap-3 bg-ivory border border-pine/10 rounded-lg px-4 py-3"
                                    >
                                        <FileText className="w-4 h-4 text-pine shrink-0" />
                                        <span className="text-sm text-charcoal truncate flex-1">{f.name}</span>
                                        <span className="text-xs text-moss shrink-0">
                                            {(f.size / 1024 / 1024).toFixed(2)} MB
                                        </span>
                                        <button
                                            type="button"
                                            data-testid={`msf-file-remove-${i}`}
                                            aria-label={`Remove ${f.name}`}
                                            onClick={() =>
                                                setData((d) => ({
                                                    ...d,
                                                    files: d.files.filter((_, j) => j !== i),
                                                }))
                                            }
                                            className="text-moss hover:text-red-800 transition-colors"
                                        >
                                            <X className="w-4 h-4" />
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                        <p className="mt-5 text-xs leading-relaxed text-moss bg-sage/70 border border-pine/10 rounded-lg px-4 py-3" data-testid="msf-upload-note">
                            {t("form.uploadDevNote", "Development note: in this version, documents are not uploaded or stored on our servers — only file names and sizes are recorded with your enquiry. Secure document storage will be enabled before launch, and our team will request your files through a safe channel in the meantime.")}
                        </p>
                    </fieldset>
                )}

                {step === 3 && (
                    <fieldset data-testid="msf-step-4">
                        <legend className="font-serif font-medium text-2xl sm:text-3xl text-forest">
                            {t("form.legend4", "A few practical details")}
                        </legend>
                        <div className="mt-6 grid sm:grid-cols-2 gap-5">
                            <div>
                                <label htmlFor="msf-timeframe" className="label-field">{t("form.timeframeLabel", "When are you hoping to travel?")}</label>
                                <select id="msf-timeframe" data-testid="msf-field-travelTimeframe" className="input-field"
                                    value={data.travelTimeframe} onChange={(e) => set("travelTimeframe", e.target.value)}>
                                    <option value="">{t("form.selectTimeframe", "Select a timeframe")}</option>
                                    {TIMEFRAMES.map((tf, i) => (
                                        <option key={tf} value={tf}>{t(`form.timeframe${i + 1}`, tf)}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label htmlFor="msf-accompanying" className="label-field">{t("form.accompanyingLabel", "Who will accompany you?")}</label>
                                <input id="msf-accompanying" data-testid="msf-field-accompanying" className="input-field"
                                    value={data.accompanying} onChange={(e) => set("accompanying", e.target.value)}
                                    placeholder={t("form.accompanyingPlaceholder", "e.g. Spouse, daughter, alone")} />
                            </div>
                            <div className="sm:col-span-2">
                                <label htmlFor="msf-notes" className="label-field">{t("form.notesLabel", "Anything else we should know?")}</label>
                                <textarea id="msf-notes" data-testid="msf-field-notes" rows={4} className="input-field resize-y"
                                    value={data.notes} onChange={(e) => set("notes", e.target.value)}
                                    placeholder={t("form.notesPlaceholder", "Language preferences, accessibility needs, questions — anything at all.")} />
                            </div>
                        </div>
                    </fieldset>
                )}

                {step === 4 && (
                    <fieldset data-testid="msf-step-5">
                        <legend className="font-serif font-medium text-2xl sm:text-3xl text-forest">
                            {t("form.legend5", "Review & submit")}
                        </legend>
                        <dl className="mt-6 grid sm:grid-cols-2 gap-x-8 gap-y-4 text-sm" data-testid="msf-review">
                            {[
                                [t("form.fullName", "Full name"), data.fullName],
                                [t("form.email", "Email"), data.email],
                                [t("form.phone", "Phone / WhatsApp"), data.phone],
                                [t("form.country", "Country"), data.country],
                                [t("form.city", "City"), data.city],
                                [t("form.dob", "Date of birth"), data.dob],
                                [t("form.areaOfCare", "Area of care"), data.specialty],
                                [t("form.reasonLabel", "Reason"), data.reason],
                                [t("form.timeframeLabel", "Travel timeframe"), data.travelTimeframe],
                                [t("form.accompanyingLabel", "Accompanying"), data.accompanying],
                                [t("form.conditionLabel", "Condition / concern"), data.condition],
                                [t("form.diagnosisLabel", "Current diagnosis / treatment"), data.diagnosis],
                                [t("form.documents", "Documents"), data.files.length ? t("form.filesValue", "{n} file(s): {names}").replace("{n}", data.files.length).replace("{names}", data.files.map(f => f.name).join(", ")) : t("form.noneSelected", "None selected")],
                                [t("form.notes", "Notes"), data.notes],
                            ]
                                .filter(([, v]) => v)
                                .map(([label, value]) => (
                                    <div key={label} className="border-t border-pine/10 pt-3">
                                        <dt className="text-[11px] font-bold uppercase tracking-widest2 text-moss">
                                            {label}
                                        </dt>
                                        <dd className="mt-1 text-charcoal/85 leading-relaxed">{value}</dd>
                                    </div>
                                ))}
                        </dl>

                        <label htmlFor="msf-consent" className="flex items-start gap-3 mt-8 text-sm text-moss leading-relaxed cursor-pointer">
                            <input
                                id="msf-consent"
                                data-testid="msf-consent"
                                type="checkbox"
                                checked={data.consent}
                                onChange={(e) => set("consent", e.target.checked)}
                                className="mt-0.5 w-5 h-5 rounded border-pine/30 text-forest focus:ring-pine/30 cursor-pointer"
                            />
                            <span>
                                {t("form.consentOpinion", "I agree that UniversHeal may contact me about this enquiry and I understand my information will be handled as described in the Privacy Policy. I understand that UniversHeal International is a healthcare coordination service and does not provide medical treatment.")}
                            </span>
                        </label>
                        {err("consent")}
                        {status === "error" && (
                            <p role="alert" data-testid="msf-server-error" className="mt-5 text-sm font-semibold text-red-800 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
                                {serverError}
                            </p>
                        )}
                    </fieldset>
                )}
            </div>

            {/* navigation */}
            <div className="mt-10 flex items-center justify-between gap-4">
                {step > 0 ? (
                    <button type="button" data-testid="msf-back" onClick={back} className="btn-secondary">
                        <ArrowLeft className="w-4 h-4" />
                        {t("common.back", "Back")}
                    </button>
                ) : (
                    <span />
                )}
                {step < STEPS.length - 1 ? (
                    <button type="button" data-testid="msf-next" onClick={next} className="btn-primary group">
                        {t("common.continue", "Continue")}
                        <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </button>
                ) : (
                    <button
                        type="button"
                        data-testid="msf-submit"
                        onClick={onSubmit}
                        disabled={status === "loading"}
                        className="btn-primary group disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                        {status === "loading" ? (
                            <>
                                {t("form.sending", "Sending")} <Loader2 className="w-4 h-4 animate-spin" />
                            </>
                        ) : (
                            <>
                                {t("form.submitEnquiry", "Submit Enquiry")}
                                <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                            </>
                        )}
                    </button>
                )}
            </div>
        </div>
    );
};
